import React, { useState, useContext, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Checkbox,
  IconButton,
  Autocomplete,
  ListItemText,
  CircularProgress,
  Tooltip
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Help as HelpIcon
} from "@mui/icons-material";
// makeStyles da v4: o app usa o ThemeProvider do @material-ui/core; o `@mui/styles` (v5) não
// recebe esse tema, `theme.palette` vinha undefined e a tela /phrase-lists ficava em branco.
import { makeStyles } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { toast } from "react-toastify";
import { getTokens } from "../../styles/tokens";
import { StatusBadge } from "../ui";
import { filterOutboundConnections } from "../../utils/channels";

const useStyles = makeStyles((theme) => {
  const tk = theme.tokens || getTokens(
    theme.palette.type,
    theme.palette.primary.main
  );
  return {
  dialogSubtitle: {
    fontSize: 13,
    fontWeight: 400,
    color: tk.textSecondary,
    marginTop: 2,
    letterSpacing: 0,
  },
  // o `título + conteúdo` da v5 zera o padding-top; repete o respiro do modal padrão
  dialogContent: {
    "&&&": { paddingTop: 20 },
  },
  loadingBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 180,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 16,
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  full: { gridColumn: "1 / -1" },
  field: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
  label: { fontSize: 12.5, fontWeight: 600, lineHeight: 1.5, color: tk.textSecondary },
  help: { fontSize: 12, color: tk.textMuted, lineHeight: 1.5 },
  sectionTitle: {
    gridColumn: "1 / -1",
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 13,
    fontWeight: 600,
    color: tk.text,
    margin: "8px 0 0",
    paddingTop: 12,
    borderTop: `1px solid ${tk.line}`,
  },
  // mesma altura (40px) do campo de texto pequeno ao lado
  autocomplete: {
    "&& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
      paddingTop: 7.5,
      paddingBottom: 7.5,
    },
  },
  selectedBox: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
    minHeight: 23,
  },
  placeholder: { color: tk.textMuted },
  online: {
    color: tk.semantic.success,
  },
  offline: {
    color: tk.semantic.error,
  },
  phraseList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  phraseRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    [theme.breakpoints.down("xs")]: { flexWrap: "wrap" },
  },
  phraseText: {
    flex: "1 1 240px",
    [theme.breakpoints.down("xs")]: { flex: "1 1 100%" },
  },
  phraseType: {
    flex: "0 0 160px",
    [theme.breakpoints.down("xs")]: { flex: "1 1 120px" },
  },
  // desce o botão para a linha do campo (rótulo 18.75 + gap 6); a classe fica no
  // invólucro porque o IconButton é v5 e o estilo dele vence o makeStyles
  removeWrap: {
    flex: "none",
    display: "flex",
    alignItems: "center",
    height: 40,
    marginTop: 18.75 + 6,
    color: tk.semantic.error,
  },
  addButton: {
    alignSelf: "flex-start",
  },
  statusControl: { marginLeft: -9, marginRight: 0 },
  // a tradução vem em caixa alta ("CANCELAR"); o botão do tema não grita
  sentenceCase: {
    display: "inline-block",
    textTransform: "lowercase",
    "&::first-letter": { textTransform: "uppercase" },
  },
  };
});

const CampaignModalPhrase = ({ open, onClose, FlowCampaignId, onSave }) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  
  const companyId = user?.companyId;

  // Estados básicos
  const [campaignEditable, setCampaignEditable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(true);

  // Estados do formulário
  const [dataItem, setDataItem] = useState({
    name: "",
  });

  const [dataItemError, setDataItemError] = useState({
    name: false,
    flowId: false,
    phrases: false,
    whatsappIds: false, // NOVA VALIDAÇÃO
  });

  // Estado para múltiplas frases
  const [phrases, setPhrases] = useState([
    { text: "", type: "exact" }
  ]);

  // Estados para flows e whatsapp
  const [flowSelected, setFlowSelected] = useState(null);
  const [flowsData, setFlowsData] = useState([]);
  const [flowsDataComplete, setFlowsDataComplete] = useState([]);
  
  // ALTERAÇÃO: Estado para múltiplas conexões
  const [selectedWhatsapps, setSelectedWhatsapps] = useState([]);
  const [whatsApps, setWhatsApps] = useState([]);

  // Buscar flows
  const getFlows = async () => {
    try {
      const flows = await api.get("/flowbuilder");
      if (isMounted.current) {
        setFlowsDataComplete(flows.data.flows || []);
        setFlowsData((flows.data.flows || []).map((flow) => flow.name));
      }
      return flows.data.flows || [];
    } catch (error) {
      console.error("Erro ao buscar flows:", error);
      if (isMounted.current) {
        toast.error("Erro ao carregar fluxos");
      }
      return [];
    }
  };

  // Buscar WhatsApps
  const getWhatsApps = async () => {
    try {
      const response = await api.get("/whatsapp");
      if (isMounted.current) {
        setWhatsApps(filterOutboundConnections(response.data || []));
      }
    } catch (error) {
      console.error("Erro ao buscar WhatsApps:", error);
      if (isMounted.current) {
        toast.error("Erro ao carregar conexões");
      }
    }
  };

  // Carregar dados se for edição
  const detailsPhrase = async (flows) => {
    if (!FlowCampaignId || !isMounted.current) return;
    
    setLoading(true);
    try {
      console.log(`Buscando dados da campanha ID: ${FlowCampaignId}`);
      const response = await api.get(`/flowcampaign/${FlowCampaignId}`);
      console.log("Resposta da API detailsPhrase:", response.data);
      
      const details = response.data;
      
      if (!isMounted.current) return;
      
      // Preencher nome da campanha
      setDataItem({
        name: details.name || "",
      });
      
      // Preencher status
      setActive(details.status !== false);
      
      // ALTERAÇÃO: Preencher WhatsApps selecionados (múltiplos)
      if (details.whatsappIds && Array.isArray(details.whatsappIds)) {
        setSelectedWhatsapps(details.whatsappIds);
      } else if (details.whatsappId) {
        // Compatibilidade com formato antigo
        setSelectedWhatsapps([details.whatsappId]);
      }
      
      // Processar frases
      let parsedPhrases = [];
      try {
        if (details.phrase) {
          if (typeof details.phrase === 'string') {
            try {
              const parsed = JSON.parse(details.phrase);
              if (Array.isArray(parsed)) {
                parsedPhrases = parsed;
              } else {
                parsedPhrases = [{ text: details.phrase, type: 'exact' }];
              }
            } catch {
              parsedPhrases = [{ text: details.phrase, type: 'exact' }];
            }
          } else if (Array.isArray(details.phrase)) {
            parsedPhrases = details.phrase;
          } else if (typeof details.phrase === 'object') {
            parsedPhrases = [details.phrase];
          }
        }
        
        parsedPhrases = parsedPhrases.map(phrase => ({
          text: phrase.text || phrase,
          type: phrase.type || 'exact'
        }));
        
        if (parsedPhrases.length === 0) {
          parsedPhrases = [{ text: "", type: "exact" }];
        }
        
        console.log("Frases processadas:", parsedPhrases);
        setPhrases(parsedPhrases);
      } catch (error) {
        console.error("Erro ao processar frases:", error);
        setPhrases([{ text: "", type: "exact" }]);
      }
      
      // Selecionar flow
      if (details.flowId && flows && flows.length > 0) {
        const foundFlow = flows.find((flow) => flow.id === details.flowId);
        if (foundFlow) {
          console.log("Flow encontrado:", foundFlow.name);
          setFlowSelected(foundFlow.name);
        } else {
          console.warn("Flow não encontrado para ID:", details.flowId);
        }
      }
      
    } catch (error) {
      console.error("Erro ao carregar detalhes:", error);
      if (isMounted.current) {
        toast.error("Erro ao carregar dados da campanha");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // Resetar estados quando modal abre/fecha
  const resetForm = () => {
    setDataItem({ name: "" });
    setPhrases([{ text: "", type: "exact" }]);
    setFlowSelected(null);
    setSelectedWhatsapps([]); // ALTERAÇÃO: Resetar array
    setActive(true);
    clearErrors();
  };

  // Inicialização
  useEffect(() => {
    isMounted.current = true;
    
    const initData = async () => {
      if (open) {
        console.log("Modal aberto. FlowCampaignId:", FlowCampaignId);
        
        resetForm();
        
        const flows = await getFlows();
        await getWhatsApps();
        
        if (FlowCampaignId) {
          await detailsPhrase(flows);
        }
      }
    };
    
    initData();

    return () => {
      isMounted.current = false;
    };
  }, [open, FlowCampaignId]);

  // Função para adicionar nova frase
  const addPhraseField = () => {
    setPhrases(prevPhrases => [...prevPhrases, { text: "", type: "exact" }]);
  };

  // Função para remover frase
  const removePhraseField = (index) => {
    if (phrases.length > 1) {
      setPhrases(prevPhrases => prevPhrases.filter((_, i) => i !== index));
    }
  };

  // Função para atualizar frase específica
  const updatePhrase = (index, field, value) => {
    setPhrases(prevPhrases => {
      const newPhrases = [...prevPhrases];
      if (newPhrases[index]) {
        newPhrases[index] = { ...newPhrases[index], [field]: value };
      }
      return newPhrases;
    });
  };

  // Validação
  const validateForm = () => {
    const errors = {
      name: !dataItem.name.trim(),
      flowId: !flowSelected,
      phrases: phrases.length === 0 || phrases.some(p => !p.text.trim()),
      whatsappIds: selectedWhatsapps.length === 0 // NOVA VALIDAÇÃO
    };
    
    setDataItemError(errors);
    return !Object.values(errors).some(error => error);
  };

  // Limpar erros
  const clearErrors = () => {
    setDataItemError({
      name: false,
      flowId: false,
      phrases: false,
      whatsappIds: false,
    });
  };

  // Handler para mudanças no campo nome
  const handleNameChange = (event) => {
    const value = event.target.value;
    setDataItem(prev => ({ ...prev, name: value }));
    
    if (value.trim() && dataItemError.name) {
      setDataItemError(prev => ({ ...prev, name: false }));
    }
  };

  // NOVA FUNÇÃO: Handler para mudanças na seleção de WhatsApps
  const handleWhatsappChange = (event) => {
    const value = event.target.value;
    setSelectedWhatsapps(typeof value === 'string' ? value.split(',') : value);
    
    if (value.length > 0 && dataItemError.whatsappIds) {
      setDataItemError(prev => ({ ...prev, whatsappIds: false }));
    }
  };

  // Salvar
  const handleSave = async () => {
    if (!validateForm()) {
      toast.error("Por favor, preencha todos os campos obrigatórios");
      return;
    }

    setLoading(true);
    
    try {
      const flowIdSelected = flowsDataComplete.find(flow => flow.name === flowSelected)?.id;
      
      if (!flowIdSelected) {
        toast.error("Fluxo selecionado não encontrado");
        return;
      }

      // Filtrar frases vazias
      const validPhrases = phrases.filter(p => p.text.trim());
      
      const payload = {
        name: dataItem.name.trim(),
        flowId: flowIdSelected,
        phrases: validPhrases,
        whatsappIds: selectedWhatsapps, // ALTERAÇÃO: Enviar array
        status: active,
        ...(FlowCampaignId && { id: FlowCampaignId })
      };

      console.log("Payload para salvar:", payload);

      if (FlowCampaignId) {
        await api.put("/flowcampaign", payload);
        toast.success("Campanha atualizada com sucesso!");
      } else {
        await api.post("/flowcampaign", payload);
        toast.success("Campanha criada com sucesso!");
      }

      onSave && onSave();
      handleClose();
      
    } catch (error) {
      console.error("Erro ao salvar:", error);
      toast.error("Erro ao salvar campanha");
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // Fechar modal
  const handleClose = () => {
    if (!isMounted.current) return;
    
    resetForm();
    onClose();
  };

  // Verificação de segurança antes de renderizar
  if (!user || !companyId) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        {campaignEditable ? (
          <>
            {FlowCampaignId
              ? `${i18n.t("campaignsPhrase.editCampaignWithFlowByPhrase")}`
              : `${i18n.t("campaignsPhrase.newCampaignWithFlowByPhrase")}`}
          </>
        ) : (
          <>{`${i18n.t("campaigns.dialog.readonly")}`}</>
        )}
        <div className={classes.dialogSubtitle}>
          O fluxo começa quando uma das conexões recebe uma das frases abaixo.
        </div>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <div className={classes.loadingBox}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <div className={classes.formGrid}>
            {/* Nome da Campanha */}
            <div className={classes.field}>
              <label className={classes.label} htmlFor="campaign-phrase-name">
                {i18n.t("campaignsPhrase.phraseTriggerName")}
              </label>
              <TextField
                id="campaign-phrase-name"
                name="name"
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Nome da campanha"
                error={dataItemError.name}
                value={dataItem.name}
                onChange={handleNameChange}
                helperText={dataItemError.name ? "Nome é obrigatório" : ""}
                disabled={loading}
              />
            </div>

            {/* Seleção do Fluxo */}
            <div className={classes.field}>
              <label className={classes.label} htmlFor="campaign-phrase-flow">
                {i18n.t("campaignsPhrase.chooseAStream")}
              </label>
              <Autocomplete
                id="campaign-phrase-flow"
                className={classes.autocomplete}
                size="small"
                fullWidth
                value={flowSelected}
                onChange={(event, newValue) => {
                  setFlowSelected(newValue);
                  if (newValue && dataItemError.flowId) {
                    setDataItemError(prev => ({ ...prev, flowId: false }));
                  }
                }}
                options={flowsData}
                disabled={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Selecione um fluxo"
                    variant="outlined"
                    error={dataItemError.flowId}
                    helperText={dataItemError.flowId ? "Fluxo é obrigatório" : ""}
                  />
                )}
              />
            </div>

            {/* Seleção de Múltiplas Conexões WhatsApp */}
            <div className={`${classes.field} ${classes.full}`}>
              <label className={classes.label} id="campaign-phrase-whatsapps-label">
                {i18n.t("campaignsPhrase.selectAConnection")} (Múltiplas)
              </label>
              <FormControl fullWidth variant="outlined" size="small" error={dataItemError.whatsappIds}>
                <Select
                  multiple
                  displayEmpty
                  labelId="campaign-phrase-whatsapps-label"
                  value={selectedWhatsapps}
                  onChange={handleWhatsappChange}
                  disabled={loading}
                  renderValue={(selected) => (
                    <div className={classes.selectedBox}>
                      {selected.length === 0 && (
                        <span className={classes.placeholder}>Conexões WhatsApp</span>
                      )}
                      {selected.map((whatsappId) => {
                        const whatsapp = whatsApps.find(w => w.id === whatsappId);
                        return (
                          <StatusBadge
                            key={whatsappId}
                            color={whatsapp?.status === "CONNECTED" ? "success" : "neutral"}
                            label={whatsapp ? whatsapp.name : `ID: ${whatsappId}`}
                          />
                        );
                      })}
                    </div>
                  )}
                >
                  {whatsApps?.length > 0 &&
                    whatsApps.map((whatsapp) => (
                      <MenuItem key={whatsapp.id} value={whatsapp.id}>
                        <Checkbox size="small" checked={selectedWhatsapps.indexOf(whatsapp.id) > -1} />
                        <ListItemText
                          primary={
                            <span style={{ fontSize: 14 }}>
                              {whatsapp.name} &nbsp;
                              <span
                                className={
                                  whatsapp.status === "CONNECTED"
                                    ? classes.online
                                    : classes.offline
                                }
                              >
                                ({whatsapp.status})
                              </span>
                            </span>
                          }
                        />
                      </MenuItem>
                    ))}
                </Select>
                {dataItemError.whatsappIds && (
                  <FormHelperText>
                    Pelo menos uma conexão deve ser selecionada
                  </FormHelperText>
                )}
              </FormControl>

              {/* Informação sobre conexões selecionadas */}
              {selectedWhatsapps.length > 0 && (
                <span className={classes.help}>
                  <strong>{selectedWhatsapps.length} conexão(ões) selecionada(s):</strong>{" "}
                  A campanha será executada quando qualquer uma dessas conexões receber mensagens que façam match com as frases configuradas.
                </span>
              )}
            </div>

            {/* Seção de Frases Múltiplas */}
            <div className={classes.sectionTitle}>
              Frases/Palavras que disparam o fluxo
              <Tooltip title="Exata: a mensagem inteira precisa ser igual à frase. Parcial: basta a frase aparecer na mensagem.">
                <IconButton size="small" aria-label="Ajuda sobre os tipos de correspondência">
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>

            <div className={`${classes.help} ${classes.full}`}>
              <strong>Correspondência Exata:</strong> A mensagem deve ser idêntica à frase configurada.<br/>
              <strong>Correspondência Parcial:</strong> A frase pode estar contida em qualquer parte da mensagem.
            </div>

            <div className={`${classes.phraseList} ${classes.full}`}>
              {phrases.map((phrase, index) => (
                <div key={index} className={classes.phraseRow}>
                  <div className={`${classes.field} ${classes.phraseText}`}>
                    <label className={classes.label} htmlFor={`campaign-phrase-text-${index}`}>
                      {`Frase ${index + 1}`}
                    </label>
                    <TextField
                      id={`campaign-phrase-text-${index}`}
                      value={phrase.text}
                      onChange={(e) => {
                        updatePhrase(index, 'text', e.target.value);
                        if (e.target.value.trim() && dataItemError.phrases) {
                          setDataItemError(prev => ({ ...prev, phrases: false }));
                        }
                      }}
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="Digite a frase ou palavra-chave"
                      error={dataItemError.phrases && !phrase.text.trim()}
                      helperText={
                        dataItemError.phrases && !phrase.text.trim() 
                          ? "Frase é obrigatória" 
                          : ""
                      }
                      disabled={loading}
                    />
                  </div>

                  <div className={`${classes.field} ${classes.phraseType}`}>
                    <label className={classes.label} id={`campaign-phrase-type-label-${index}`}>
                      Tipo
                    </label>
                    <FormControl fullWidth variant="outlined" size="small">
                      <Select
                        labelId={`campaign-phrase-type-label-${index}`}
                        value={phrase.type}
                        onChange={(e) => updatePhrase(index, 'type', e.target.value)}
                        disabled={loading}
                      >
                        <MenuItem value="exact">Exata</MenuItem>
                        <MenuItem value="partial">Parcial</MenuItem>
                      </Select>
                    </FormControl>
                  </div>

                  {phrases.length > 1 && (
                    <Tooltip title="Remover frase">
                      <span className={classes.removeWrap}>
                        <IconButton
                          size="small"
                          onClick={() => removePhraseField(index)}
                          color="inherit"
                          aria-label={`Remover frase ${index + 1}`}
                          disabled={loading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </div>
              ))}

              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addPhraseField}
                className={classes.addButton}
                disabled={loading}
              >
                Adicionar Frase
              </Button>
            </div>

            {/* Status */}
            <div className={classes.sectionTitle}>
              {i18n.t("campaignsPhrase.status")}
            </div>
            <div className={classes.full}>
              <FormControlLabel
                className={classes.statusControl}
                control={
                  <Checkbox
                    checked={active}
                    onChange={() => setActive(prev => !prev)}
                    disabled={loading}
                  />
                }
                label={active ? "Ativa" : "Inativa"}
              />
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          color="inherit"
          onClick={handleClose}
          disabled={loading}
        >
          <span className={classes.sentenceCase}>
            {i18n.t("campaignsPhrase.cancel")}
          </span>
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? "Salvando..." : FlowCampaignId ? "Atualizar" : "Criar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignModalPhrase;
